/**
 * CR421: Normalize components shape to flatten components.components nesting.
 * 
 * The canonical shape is:
 *   { ribbon: {...}, value_box: {...}, starburst: {...}, ... }
 * 
 * But some stores persist the nested shape:
 *   { components: { ribbon: {...}, value_box: {...} }, ribbon: {...} }
 * 
 * This utility deep-merges outer with inner (inner wins for non-ribbon,
 * ribbon is deep-merged).
 */

function deepMergeObjects(
  base: Record<string, unknown>,
  patch: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const baseVal = result[key];
    if (
      value !== null && typeof value === "object" && !Array.isArray(value) &&
      baseVal !== null && typeof baseVal === "object" && !Array.isArray(baseVal)
    ) {
      result[key] = deepMergeObjects(
        baseVal as Record<string, unknown>,
        value as Record<string, unknown>
      );
    } else {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Flatten a components object that may contain a nested `components` key.
 * Returns a canonical flat components map.
 */
export function normalizeComponentsShape(
  raw: Record<string, unknown> | null | undefined
): Record<string, unknown> | null {
  if (!raw) return null;

  const inner = raw.components;
  if (!inner || typeof inner !== "object" || Array.isArray(inner)) {
    return raw;
  }

  const innerMap = inner as Record<string, unknown>;
  // Outer minus 'components' key
  const { components: _nested, ...outer } = raw;

  // Deep-merge ribbon from both levels
  const outerRibbon = outer.ribbon as Record<string, unknown> | undefined;
  const innerRibbon = innerMap.ribbon as Record<string, unknown> | undefined;
  let mergedRibbon: Record<string, unknown> | undefined;
  if (outerRibbon || innerRibbon) {
    mergedRibbon = deepMergeObjects(outerRibbon ?? {}, innerRibbon ?? {});
  }

  // Start with outer, overlay inner
  const result = deepMergeObjects(outer, innerMap);

  // Apply merged ribbon
  if (mergedRibbon) {
    result.ribbon = mergedRibbon;
  }

  return result;
}
